import { Navigate, Route, Routes } from "react-router";

import { AccrualsPage } from "@/pages/accruals";
import { AdminsPage } from "@/pages/admins";
import { AuditPage } from "@/pages/audit";
import { CommentsPage } from "@/pages/comments";
import { DashboardPage } from "@/pages/dashboard";
import { DepositsPage } from "@/pages/deposits";
import { NotFoundPage } from "@/pages/not-found";
import { RobotAnalyticsPage } from "@/pages/robot-analytics";
import { RobotFormPage } from "@/pages/robot-form";
import { RobotsPage } from "@/pages/robots";
import { SettingsPage } from "@/pages/settings";
import { UserDetailPage } from "@/pages/user-detail";
import { UsersPage } from "@/pages/users";
import { WithdrawalsPage } from "@/pages/withdrawals";
import { ROUTES } from "@/shared/config";

import { useAuth } from "@/entities/session";

/**
 * Роуты суперадмина закрыты и здесь, и на сервере.
 *
 * Клиентская проверка не защита — токен разбирается в браузере. Она нужна
 * ради другого: оператор, открывший `/settings` по ссылке из чата, должен
 * увидеть понятный 404, а не пустой экран с тостом «нет прав» поверх формы,
 * которую всё равно нельзя сохранить.
 */
function SuperadminOnly({ children }: { children: React.ReactElement }) {
  const { isSuperadmin } = useAuth();
  return isSuperadmin ? children : <NotFoundPage />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path={ROUTES.dashboard} element={<DashboardPage />} />
      <Route path={ROUTES.deposits} element={<DepositsPage />} />
      <Route path={ROUTES.withdrawals} element={<WithdrawalsPage />} />
      <Route path={ROUTES.users} element={<UsersPage />} />
      <Route path="/users/:id" element={<UserDetailPage />} />
      {/* Модерация отзывов — работа оператора, как и заявки: не в
          SuperadminOnly. */}
      <Route path={ROUTES.comments} element={<CommentsPage />} />
      <Route path={ROUTES.audit} element={<AuditPage />} />

      {/* Статический сегмент объявлен до динамического: `/robots/new` должен
          открывать форму создания, а не карточку робота с id «new». React
          Router ранжирует статику выше сам, но полагаться на это молча не
          стоит — порядок закреплён тестом роутинга. */}
      <Route
        path={ROUTES.robots}
        element={
          <SuperadminOnly>
            <RobotsPage />
          </SuperadminOnly>
        }
      />
      <Route
        path={ROUTES.robotNew}
        element={
          <SuperadminOnly>
            <RobotFormPage />
          </SuperadminOnly>
        }
      />
      {/* Аналитика объявлена ДО «/robots/:id»: иначе форма перехватит
          `/robots/1/analytics` как робота с id «1/analytics». */}
      <Route
        path="/robots/:id/analytics"
        element={
          <SuperadminOnly>
            <RobotAnalyticsPage />
          </SuperadminOnly>
        }
      />
      <Route
        path="/robots/:id"
        element={
          <SuperadminOnly>
            <RobotFormPage />
          </SuperadminOnly>
        }
      />
      <Route
        path={ROUTES.accruals}
        element={
          <SuperadminOnly>
            <AccrualsPage />
          </SuperadminOnly>
        }
      />
      <Route
        path={ROUTES.settings}
        element={
          <SuperadminOnly>
            <SettingsPage />
          </SuperadminOnly>
        }
      />
      <Route
        path={ROUTES.admins}
        element={
          <SuperadminOnly>
            <AdminsPage />
          </SuperadminOnly>
        }
      />

      {/* Форма входа под авторизованным пользователем ведёт на сводку: иначе
          кнопка «назад» после логина показывает пустую форму. */}
      <Route path={ROUTES.login} element={<Navigate to={ROUTES.dashboard} replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
